import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { BarChart3, Eye, PhoneCall, Share2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { fetchMyListings } from "@/lib/listings";
import { countBy, fetchOwnerEvents, groupByDay, perListing } from "@/lib/stats";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/statistika")({
  head: () => ({
    meta: [
      { title: "Satıcı statistikası — Albazar.az" },
      {
        name: "description",
        content:
          "Elanlarınızın baxış sayı, əlaqə klikləri və paylaşımları üzrə 7 və 30 günlük statistika.",
      },
      { property: "og:title", content: "Satıcı statistikası — Albazar.az" },
      {
        property: "og:description",
        content: "Baxış, zəng, WhatsApp və mesaj kliklərinin gündəlik dinamikası.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StatsPage,
});

function StatsPage() {
  const { user } = useAuth();
  const [days, setDays] = useState<7 | 30>(7);

  const events = useQuery({
    queryKey: ["listing-events", user?.id, days],
    queryFn: () => fetchOwnerEvents(user!.id, days),
    enabled: Boolean(user?.id),
  });

  const listings = useQuery({
    queryKey: ["my-listings", user?.id],
    queryFn: () => fetchMyListings(user!.id),
    enabled: Boolean(user?.id),
  });

  const data = events.data ?? [];
  const points = useMemo(() => groupByDay(data, days), [data, days]);
  const max = Math.max(1, ...points.map((p) => p.views + p.contacts));
  const byListing = useMemo(() => perListing(data), [data]);

  const totals = {
    views: countBy(data, "view"),
    contacts: countBy(data, "phone") + countBy(data, "whatsapp") + countBy(data, "message"),
    shares: countBy(data, "share"),
  };

  return (
    <div className="container-x pb-20 pt-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-extrabold">
          <BarChart3 className="size-6 text-info" /> Satıcı statistikası
        </h1>
        <div className="flex gap-2">
          {([7, 30] as const).map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={cn(
                "h-9 rounded-full px-4 text-sm font-bold transition-colors",
                days === d
                  ? "bg-primary text-primary-foreground"
                  : "border border-border bg-card hover:bg-muted",
              )}
            >
              {d} gün
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={<Eye className="size-4" />} label="Baxış" value={totals.views} />
        <StatCard icon={<PhoneCall className="size-4" />} label="Əlaqə" value={totals.contacts} />
        <StatCard icon={<Share2 className="size-4" />} label="Paylaşım" value={totals.shares} />
      </div>

      <div className="mt-4 rounded-3xl border border-border bg-card p-4 shadow-card">
        <p className="mb-3 text-sm font-bold">Gündəlik dinamika</p>
        <div className="flex h-40 items-end gap-1">
          {points.map((p) => (
            <div key={p.day} className="flex flex-1 flex-col justify-end gap-0.5" title={`${p.day}: ${p.views} baxış, ${p.contacts} əlaqə`}>
              <div
                className="w-full rounded-t bg-info/70"
                style={{ height: `${(p.views / max) * 100}%` }}
              />
              <div
                className="w-full rounded-b bg-primary/80"
                style={{ height: `${(p.contacts / max) * 100}%` }}
              />
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="size-2.5 rounded-full bg-info/70" /> Baxış
          </span>
          <span className="flex items-center gap-1">
            <span className="size-2.5 rounded-full bg-primary/80" /> Əlaqə
          </span>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <p className="text-sm font-bold">Elanlar üzrə</p>
        {(listings.data ?? []).map((l) => {
          const row = byListing.get(String(l.id)) ?? { views: 0, contacts: 0, shares: 0 };
          return (
            <Link
              key={l.id}
              to="/elan/$id"
              params={{ id: String(l.id) }}
              className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3 text-sm shadow-card"
            >
              <span className="min-w-0 flex-1 truncate font-semibold">{l.title}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                👁 {row.views} · 📞 {row.contacts} · ↗ {row.shares}
              </span>
            </Link>
          );
        })}
        {(listings.data ?? []).length === 0 && (
          <p className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            Hələ elanınız yoxdur.
          </p>
        )}
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3 text-center shadow-card">
      <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
        {icon} {label}
      </div>
      <p className="mt-1 text-xl font-extrabold">{value}</p>
    </div>
  );
}
